import styled from '@emotion/styled'
import { AdminSite } from '@pubstack/common/src/site'
import { AdmSiteConfig } from '@pubstack/common/src/siteConfig'
import { FunctionComponent, useEffect, useMemo, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { useAdminSites } from '~/api/admin/admin-site.api'
import { useKleanadsSiteConfig, useUpdateKleanadsSiteConfig } from '~/api/admin/kleanads-config.api'
import { Input } from '~/components/Input'
import { Message } from '~/components/Message'
import { SelectableOptionsPopover } from '~/components/SelectableOptionsPopover'
import { Skeleton } from '~/components/Skeleton'
import { JSONEditor } from '../PureJSONEditor'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const InputWrapper = styled.div`
  width: 100%;
`

export type DisplayedSiteConfig = Omit<AdmSiteConfig, 'siteId' | 'scopeId'>

export const AdminSiteConfigEdit: FunctionComponent = () => {
  const { scopeId } = useParams()
  const [search, setSearch] = useState<string>('')
  const [selectedTagId, setSelectedTagId] = useState<string>('')
  const [isSuggestionsOpen, setIsSuggestionOpen] = useState<boolean>(false)
  const [currentTag, setCurrentTag] = useState('')
  const [lastSearch, setLastSearch] = useState('')

  const { control, setValue } = useForm({
    defaultValues: {
      site: '',
    },
  })
  const siteSearch = useWatch({ control, name: 'site' })

  const { data: tags, isLoading: isTagsLoading } = useAdminSites(scopeId ?? '', search)
  const { data: siteConfigData, isLoading: isSiteConfigLoading } = useKleanadsSiteConfig(scopeId ?? '', selectedTagId)
  const { mutateAsync: updateSiteConfig } = useUpdateKleanadsSiteConfig(scopeId ?? '', selectedTagId, {
    successToast: `The site config for ${selectedTagId} has been updated`,
    errorToast: 'An error has occurred, please retry later.',
  })

  const siteConfig = useMemo(() => {
    if (!siteConfigData) return undefined
    if (!siteConfigData.siteId) return {}
    const { siteId: _siteId, scopeId: _scopeId, ...rest } = siteConfigData
    return rest
  }, [siteConfigData])

  useEffect(() => {
    if (lastSearch !== siteSearch) {
      const timeout = setTimeout(() => {
        setSearch(siteSearch)
        setLastSearch(siteSearch)
      }, 400)

      return () => clearTimeout(timeout)
    }
  }, [siteSearch, lastSearch])

  const onTagSelected = (tagId: AdminSite['id']) => {
    setSelectedTagId(tagId)
  }

  const onSiteConfigUpdate = async (config: DisplayedSiteConfig) => {
    await updateSiteConfig({ ...config, siteId: selectedTagId, scopeId: scopeId ?? '' })
  }

  return (
    <Wrapper>
      <Message
        status={'warning'}
        title={'Warning'}
        description={
          <Wrapper>
            <span>Site config edition, please be careful.</span>
            <span>Be wary that NO VALIDATION is done, only on JSON format.</span>
            <span>Double check everything you are doing.</span>
          </Wrapper>
        }
      />

      <SelectableOptionsPopover
        open={isSuggestionsOpen}
        options={(tags ?? []).map((e) => ({ value: e.id, label: e.name, trackingLabel: 'adm-siteconfig-set' }))}
        optionsLoading={isTagsLoading}
        setOpen={setIsSuggestionOpen}
        trigger={
          <InputWrapper>
            <Input
              iconLeft={'search'}
              name={'site'}
              onClear={() => {
                setCurrentTag('')
                setValue('site', '')
              }}
              control={control}
              label={'Search a site...'}
              labelIsPlaceholder
            />
          </InputWrapper>
        }
        onChange={(e) => {
          setValue('site', e.label as string)
          setCurrentTag(e.label as string)
          onTagSelected(e.value as string)
        }}
        maxWidthPopoverSameAsTrigger={true}
      />

      {isSiteConfigLoading ? <Skeleton bigger /> : currentTag && siteConfig && <JSONEditor<DisplayedSiteConfig> data={siteConfig} onValidate={onSiteConfigUpdate} statusUpdate={() => {}} />}
    </Wrapper>
  )
}
